/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { useRetracked } from 'js/lib/retracked';
import user from 'js/lib/user';

import type { ButtonProps } from '@coursera/cds-core';
import { Button, Grid, PageGridContainer, PageNotification, Typography2 } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ShareProfileLinkDialog from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/ShareProfileLinkDialog';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

const styles = {
  privacyNotification: css`
    margin: 0 calc(var(--cds-spacing-150) * -1) var(--cds-spacing-300);
  `,
  privacyBtn: css`
    margin-top: calc(var(--cds-spacing-50) * -1);
  `,
};

type PrivacyNotificationProps = {
  inlineActionHref?: string;
  linkType?: 'internal' | 'external';
};

const PrivacyNotification = ({ inlineActionHref, linkType }: PrivacyNotificationProps) => {
  const { editable, profileState, isProfileLoading, sharedEventingV3Data } = useContext(ProfileContext);
  const [showPrivacySettingsDialog, setShowPrivacySettingsDialog] = useState<boolean>(false);
  const retrackedTrack = useRetracked();
  const track = useTracker();

  const trackingData = {
    button: {
      name: EVENTS.updatePrivacySettingsBanner,
      ...(inlineActionHref && linkType
        ? {
            linkType,
            linkURL: inlineActionHref,
          }
        : {}),
    },
    ...sharedEventingV3Data,
  };

  const visibilityTrackingRef: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker(
    'view_button',
    trackingData
  );

  const isViewingOwnProfile = profileState?.externalId === user.get()?.external_id;
  const isPrivate = !profileState?.showToPublic;
  const includedInTalentPipeline = profileState?.includedInTalentPipeline ?? false;

  if (isProfileLoading) return null;
  if ((!editable && !isViewingOwnProfile) || !isPrivate) return null;

  const trackClick = () => {
    retrackedTrack({
      trackingName: EVENTS.updatePrivacySettingsBanner,
      trackingData: {},
      action: 'click',
    });

    track('click_button', trackingData);
  };

  const buttonLinkProps: Partial<ButtonProps> = {
    ...(inlineActionHref
      ? {
          component: 'a',
          href: inlineActionHref,
          onClick: trackClick,
        }
      : {
          onClick: () => {
            trackClick();
            setShowPrivacySettingsDialog(true);
          },
        }),
  };

  const message = includedInTalentPipeline ? (
    <Typography2 component="p">
      <strong>{_t('Your profile is only visible to recruiters on Coursera Hiring Solutions.')}</strong>{' '}
      {_t('To share your profile link as part of your job search, update your visibility settings.')}
    </Typography2>
  ) : (
    <Typography2 component="p">
      <strong>{_t('Your profile is currently private.')}</strong>{' '}
      {_t('To share your profile link, update your visibility settings.')}
    </Typography2>
  );

  return (
    <PageGridContainer maxWidth="lg">
      <Grid item xs={12} sm>
        <PageNotification
          css={styles.privacyNotification}
          severity="warning"
          inlineAction={
            <TrackedDiv
              trackingName={EVENTS.updatePrivacySettingsBanner}
              withVisibilityTracking
              requireFullyVisible={false}
              trackClicks={false}
            >
              <Button
                css={styles.privacyBtn}
                variant="ghost"
                size="small"
                ref={visibilityTrackingRef}
                {...buttonLinkProps}
              >
                {_t('Update visibility settings')}
              </Button>
            </TrackedDiv>
          }
        >
          {message}
        </PageNotification>
        {showPrivacySettingsDialog && (
          <ShareProfileLinkDialog
            isOpen={showPrivacySettingsDialog}
            onClose={() => setShowPrivacySettingsDialog(false)}
            openPrivacySettingsDialog={true}
          />
        )}
      </Grid>
    </PageGridContainer>
  );
};

export default PrivacyNotification;
